import moment from 'moment'
import AuthMixin from '@/components/mixins/auth-mixin'
import YearlyCalendarComp from '@/components/common/yearly-calendar/yearly-calendar-tmpl'
import * as JsSearch from 'js-search'

export default {
  name: 'userInvoice',
  inject: [
    '$utils',
    '$api',
    '$apiError'
  ],
  components: {
    YearlyCalendar: YearlyCalendarComp
  },
  props: {
    userInfoProp: {
      default () { return null }
    },
    accessorComponentProp: {
      type: String,
      default () { return 'purchase' }
    }
  },
  watch: {
    userInfoProp (newVal) {
      this.userInfo = this.$utils.clone(newVal)
    }
  },
  mixins: [AuthMixin],
  data () {
    return {
      accessorComponent: this.accessorComponentProp,
      userInfo: this.$utils.clone(this.userInfoProp),
      error: '',
      panelHeaderIconClass: { icon: true },
      panelHeaderTitle: this.$t('label.invoice', 2),
      invoiceList: null,
      selectedMonth: {},
      invoices: null,
      invoicesFilterByYearMonth: null,
      invoiceListToShow: [],
      monthsWithNoInvoice: [],
      calendarYearOptions: [],
      calendarYear: moment().format('YYYY'),
      calendarMonth: null
    }
  },
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('user-invoice-change', { action, payload: { data } })
      }
    },
    getInvoices (params) {
      const apiRequest = () => {
        this.$utils.mainLoader(true)

        this.$api.get(`${CONFIG.API_BASE_URL}/invoices`)
          .then((response) => {
            this.invoiceList = this.filterInvoiceListData(response.data.invoices)
            this.drawInvoicesByYearMonth()

            this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
      }
      apiRequest()
    },
    filterInvoiceListData (invoiceList) {
      const newInvoiceList = this.$utils.clone(invoiceList)

      newInvoiceList.filter((invoice, indx) => {
        const copyInvoice = this.$utils.clone(invoice)
        delete copyInvoice.items

        const calcSubtotal = copyInvoice.subtotal / 100
        const calcAmount = copyInvoice.amount / 100

        invoice._edit = this.$utils.clone(copyInvoice)
        invoice._edit.vat = `${copyInvoice.vat / 100}`
        invoice._edit.amount = calcAmount
        invoice._edit.amountWithCurrency = `${copyInvoice.currency} ${calcAmount.toFixed(2)}`
        invoice._edit.billingDate = moment(copyInvoice.billingDate).clone().format('MMM DD YYYY')
        invoice._edit.subtotal = calcSubtotal
        invoice._edit.subtotalWithCurrency = `${copyInvoice.currency} ${calcSubtotal.toFixed(2)}`
        invoice._edit.dateYearMonthFormat = moment(copyInvoice.billingDate).format('YYYY-MM')
        invoice._edit.dateYearFormat = moment(copyInvoice.billingDate).format('YYYY')
        invoice._edit.dateMonthFormat = moment(copyInvoice.billingDate).format('MM')

        return invoice
      })

      return newInvoiceList
    },
    drawInvoicesByYearMonth () {
      const invoiceList = this.$utils.clone(this.invoiceList)
      const calendarYearOptions = []
      const invoiceDataByYear = {}

      // Create Year Object
      for (const indx in invoiceList) {
        const yearAsGroupKey = invoiceList[indx]._edit.dateYearFormat

        if (!invoiceDataByYear.yearAsGroupKey) {
          invoiceDataByYear[yearAsGroupKey] = {}
        }

        if (calendarYearOptions.indexOf(yearAsGroupKey) < 0) {
          calendarYearOptions.push(yearAsGroupKey)
        }
      }

      calendarYearOptions.sort()

      // Create months under each year
      for (const yearAsIndx in invoiceDataByYear) {
        const searchDataPerYear = new JsSearch.Search('invoiceId')
        searchDataPerYear.addIndex(['_edit', 'dateYearFormat'])
        searchDataPerYear.addDocuments(invoiceList)

        const results = searchDataPerYear.search(yearAsIndx)

        // Create month place holder to group per month
        for (const indx in results) {
          const invoicePerMonth = results[indx]
          const yearMonthKey = invoicePerMonth._edit.dateYearMonthFormat

          if (!invoiceDataByYear.yearMonthKey) {
            invoiceDataByYear[yearAsIndx][yearMonthKey] = []
          }
        }
      }

      // Assign invoice data per year-month
      for (const yearAsIndx in invoiceDataByYear) {
        const yearData = invoiceDataByYear[yearAsIndx]

        for (const monthFormatAsIndx in yearData) {
          const searchDataPerYearMonth = new JsSearch.Search('invoiceId')
          searchDataPerYearMonth.addIndex(['_edit', 'dateYearMonthFormat'])
          searchDataPerYearMonth.addDocuments(invoiceList)

          yearData[monthFormatAsIndx] = searchDataPerYearMonth.search(monthFormatAsIndx)
        }
      }

      this.invoicesFilterByYearMonth = invoiceDataByYear
      this.calendarYearOptions = (calendarYearOptions.length > 0) ? calendarYearOptions : [moment().format('YYYY')]
    },
    updateInvoiceListToShow () {
      if (this.invoicesFilterByYearMonth[this.calendarYear] === undefined) {
        this.invoiceListToShow = []
        return
      }

      if (this.invoicesFilterByYearMonth[this.calendarYear][`${this.calendarYear}-${this.calendarMonth}`] === undefined) {
        this.invoiceListToShow = []
        return
      }

      this.invoiceListToShow = this.invoicesFilterByYearMonth[this.calendarYear][`${this.calendarYear}-${this.calendarMonth}`]

      // Show invoice details if there's only one invoice in the list
      if (this.invoiceListToShow.length === 1) {
        this.$utils.mainLoader(true)
        this.handleRowClick(this.invoiceListToShow[0])
      }
    },
    /**
     * Hook function on invoice row clicked
     * @param {Object} $event
     */
    handleRowClick ($event) {
      this.$router.push(`/settings/invoices/${$event.invoiceId}`, () => {})
    },
    /**
     * Hook function when yearly calendar sub-component changed
     * @param {Object} event
     */
    handleYearlyCalendarChanged (event) {
      if (event.action === 'CALENDAR_MONTH_SELECTED') {
        let newCalendarMonth = null

        if (event.payload.data !== null && event.payload.data.id) {
          newCalendarMonth = event.payload.data.id
        }
        this.calendarMonth = newCalendarMonth
      }

      if (event.action === 'CALENDAR_YEAR_SELECTED') {
        let newCalendarYear = null

        if (event.payload.data !== null) {
          newCalendarYear = event.payload.data
        }
        this.calendarYear = newCalendarYear
      }

      this.updateInvoiceListToShow()
    }
  },
  mounted () {
    this.getInvoices({ showDefaultInvoices: true })
  }
}
